import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, Calendar, MapPin, ChevronDown, ChevronUp, Eye, Filter, CalendarDays, Wifi, WifiOff, Target, TrendingUp, Sun, Moon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { NativeSelect } from './ui/native-select';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MinhasVendasTab = () => {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dias');
  const [expandedDays, setExpandedDays] = useState({});
  
  const [filtroMes, setFiltroMes] = useState(new Date().getMonth() + 1);
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
  const [filtroSeccao, setFiltroSeccao] = useState('Todas');

  const MESES = [
    { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' }, { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' }, { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' }
  ];

  const loadVendas = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Endpoint novo com breakdown por TURNO + progresso de pontos.
      // Fallback para /api/minhas-vendas-xd se o novo devolver estrutura inesperada.
      let response;
      try {
        response = await axios.get(`${API_URL}/api/minhas-vendas-turnos`, {
          params: { mes: filtroMes, ano: filtroAno },
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        response = await axios.get(`${API_URL}/api/minhas-vendas-xd`, {
          params: { mes: filtroMes, ano: filtroAno },
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setDados(response.data);
    } catch (error) {
      console.error('Erro ao carregar vendas:', error);
      toast.error('Erro ao carregar vendas');
      setDados(null);
    } finally {
      setLoading(false);
    }
  }, [filtroMes, filtroAno]);

  useEffect(() => {
    loadVendas();
  }, [loadVendas]);

  // Derivar dados para render
  const fonte = dados?.fonte || 'manual';
  const totais = dados?.totais || {};
  const vendasPorDia = dados?.vendas_por_dia || [];
  const porSeccao = dados?.por_seccao || {};

  // Filtro de secção local
  const seccoes = ['Todas', ...Object.keys(porSeccao)];
  
  const vendasFiltradas = filtroSeccao === 'Todas' 
    ? vendasPorDia 
    : vendasPorDia.map(dia => ({
        ...dia,
        vendas: dia.vendas.filter(v => v.seccao === filtroSeccao),
        total: dia.vendas.filter(v => v.seccao === filtroSeccao).reduce((s, v) => s + v.valor, 0),
        nr_docs: dia.vendas.filter(v => v.seccao === filtroSeccao).reduce((s, v) => s + v.nr_docs, 0),
      })).filter(dia => dia.vendas.length > 0);

  // Recalcular totais filtrados
  const totalFiltrado = filtroSeccao === 'Todas' 
    ? (totais.total_liquido || 0) 
    : vendasFiltradas.reduce((s, d) => s + d.total, 0);
  const nrVendasFiltrado = filtroSeccao === 'Todas'
    ? (totais.nr_vendas || 0)
    : vendasFiltradas.reduce((s, d) => s + d.nr_docs, 0);
  const diasComVendas = vendasFiltradas.length;
  const mediaVenda = nrVendasFiltrado > 0 ? totalFiltrado / nrVendasFiltrado : 0;

  const toggleDayExpand = (dia) => {
    setExpandedDays(prev => ({ ...prev, [dia]: !prev[dia] }));
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return 'Data não disponível';
    const data = new Date(dataStr + 'T00:00:00');
    return data.toLocaleDateString('pt-PT', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
    });
  };

  if (loading) return <div className="flex items-center justify-center py-12">Carregando...</div>;

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2" data-testid="minhas-vendas-title">
            <ShoppingBag className="h-5 w-5 text-primary" />
            Minhas Vendas
            {fonte === 'xd' ? (
              <Badge variant="outline" className="ml-2 text-xs bg-green-50 text-green-700 border-green-300">
                <Wifi className="h-3 w-3 mr-1" /> XD
              </Badge>
            ) : (
              <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-300">
                <WifiOff className="h-3 w-3 mr-1" /> Manual
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            {fonte === 'xd' 
              ? 'Dados reais do sistema de faturação (XD)' 
              : 'Dados das vendas registadas manualmente'}
          </p>
        </div>
        
        <div className="flex gap-2 bg-muted/50 p-1 rounded-lg">
          <Button variant={activeView === 'dias' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveView('dias')} className="gap-2">
            <CalendarDays className="h-4 w-4" /> Por Dias
          </Button>
          <Button variant={activeView === 'global' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveView('global')} className="gap-2">
            <Eye className="h-4 w-4" /> Resumo Global
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="bg-gradient-card border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="h-5 w-5" /> Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Mês</Label>
              <NativeSelect value={filtroMes} onChange={(e) => setFiltroMes(parseInt(e.target.value))}>
                {MESES.map(mes => <option key={mes.value} value={mes.value}>{mes.label}</option>)}
              </NativeSelect>
            </div>
            <div>
              <Label className="text-xs">Ano</Label>
              <NativeSelect value={filtroAno} onChange={(e) => setFiltroAno(parseInt(e.target.value))}>
                {[2024, 2025, 2026].map(ano => <option key={ano} value={ano}>{ano}</option>)}
              </NativeSelect>
            </div>
            <div>
              <Label className="text-xs">Secção</Label>
              <NativeSelect value={filtroSeccao} onChange={(e) => setFiltroSeccao(e.target.value)}>
                {seccoes.map(sec => <option key={sec} value={sec}>{sec}</option>)}
              </NativeSelect>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-gradient-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total do Mês</p>
            <p className="text-2xl font-bold text-success" data-testid="total-mes">€{totalFiltrado.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Nº de Vendas</p>
            <p className="text-2xl font-bold text-primary" data-testid="nr-vendas">{nrVendasFiltrado}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Média por Venda</p>
            <p className="text-2xl font-bold text-accent">€{mediaVenda.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Dias com Vendas</p>
            <p className="text-2xl font-bold text-blue-600">{diasComVendas}</p>
          </CardContent>
        </Card>
      </div>

      {/* Média por Secção */}
      {Object.keys(porSeccao).length > 0 && (
        <Card className="bg-gradient-card border-primary/10">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-3 font-medium">Média por Secção</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(porSeccao).map(([seccao, dados]) => {
                const mediaSeccao = diasComVendas > 0 ? dados.total / diasComVendas : 0;
                return (
                  <div key={seccao} className="flex flex-col p-2 rounded-lg bg-muted/30" data-testid={`media-seccao-${seccao}`}>
                    <span className="text-xs text-muted-foreground truncate">{seccao}</span>
                    <span className="text-lg font-bold text-primary">€{mediaSeccao.toFixed(2)}</span>
                    <span className="text-[10px] text-muted-foreground">{dados.nr_docs} docs · €{dados.total.toFixed(2)} total</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Créditos (NC) - apenas quando XD e com créditos */}
      {fonte === 'xd' && totais.total_creditos > 0 && (
        <Card className="bg-red-50/50 border-red-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-700">Notas de Crédito (NC)</p>
              <p className="text-xs text-red-500">Devoluções/créditos a clientes</p>
            </div>
            <p className="text-xl font-bold text-red-600">-€{totais.total_creditos.toFixed(2)}</p>
          </CardContent>
        </Card>
      )}

      {/* Vista por Dias */}
      {activeView === 'dias' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" /> Vendas por Dia
            </CardTitle>
            <p className="text-sm text-muted-foreground">Clique num dia para ver os detalhes</p>
          </CardHeader>
          <CardContent>
            {vendasFiltradas.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingBag className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground mb-2">Nenhuma venda encontrada</p>
                <p className="text-sm text-muted-foreground">Não há vendas para o período selecionado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {vendasFiltradas.map((dia) => {
                  const isExpanded = expandedDays[dia.data];
                  const nrDocs = dia.nr_docs || dia.vendas.length;
                  
                  return (
                    <div key={dia.data} className="border border-border/50 rounded-lg overflow-hidden">
                      <div 
                        className="flex items-center justify-between p-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => toggleDayExpand(dia.data)}
                        data-testid={`dia-${dia.data}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Calendar className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <p className="font-semibold capitalize">{formatarData(dia.data)}</p>
                            <p className="text-sm text-muted-foreground">
                              {nrDocs} documento{nrDocs !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xl font-bold text-success">€{dia.total.toFixed(2)}</p>
                            {dia.creditos > 0 && (
                              <p className="text-xs text-red-500">NC: -€{dia.creditos.toFixed(2)}</p>
                            )}
                          </div>
                          {isExpanded ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="p-4 bg-background border-t border-border/50 space-y-4">
                          {/* NOVO: Breakdown por TURNO com progresso de pontos */}
                          {dia.turnos && dia.turnos.length > 0 && (
                            <div className="space-y-3" data-testid={`turnos-${dia.data}`}>
                              <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                                <CalendarDays className="h-4 w-4" />
                                Turnos do dia
                              </div>
                              {dia.turnos.map((t, i) => {
                                const isManha = t.turno === 'Manhã';
                                const progresso = t.progresso_pontos;
                                return (
                                  <div
                                    key={`turno-${i}`}
                                    className={`p-3 rounded-lg border ${isManha ? 'bg-amber-50/40 border-amber-200' : 'bg-indigo-50/40 border-indigo-200'}`}
                                    data-testid={`turno-${dia.data}-${t.turno}-${t.seccao}`}
                                  >
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        {isManha ? <Sun className="h-4 w-4 text-amber-600" /> : <Moon className="h-4 w-4 text-indigo-600" />}
                                        <span className={`font-semibold ${isManha ? 'text-amber-700' : 'text-indigo-700'}`}>
                                          {t.turno}
                                        </span>
                                        <Badge variant="outline" className="text-xs">{t.seccao}</Badge>
                                        {t.entrada && t.saida && (
                                          <span className="text-xs text-muted-foreground">
                                            {t.entrada} — {t.saida}
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        <p className="text-lg font-bold text-success">€{Number(t.total).toFixed(2)}</p>
                                        <p className="text-[10px] text-muted-foreground">{t.nr_docs} docs</p>
                                      </div>
                                    </div>

                                    {progresso && (
                                      <div className="mt-2 space-y-2">
                                        <div className="flex items-center justify-between text-xs">
                                          <div className="flex items-center gap-1.5">
                                            <Target className="h-3.5 w-3.5 text-muted-foreground" />
                                            <span className="text-muted-foreground">Objectivo turno:</span>
                                            <span className="font-medium">€{Number(progresso.objetivo_turno || 0).toFixed(2)}</span>
                                          </div>
                                          <Badge
                                            variant={progresso.pontos_atual >= 3 ? 'default' : 'secondary'}
                                            className="text-xs"
                                            data-testid={`pontos-turno-${dia.data}-${t.turno}`}
                                          >
                                            {progresso.pontos_atual} {progresso.pontos_atual === 1 ? 'ponto' : 'pontos'}
                                          </Badge>
                                        </div>
                                        {progresso.proximo_ponto !== null && progresso.falta !== null ? (
                                          <div className="p-2 rounded-md bg-white/60 border border-border/40">
                                            <div className="flex items-center justify-between text-xs mb-1">
                                              <span className="text-muted-foreground">
                                                Falta p/ <strong>{progresso.proximo_ponto} {progresso.proximo_ponto === 1 ? 'ponto' : 'pontos'}</strong> (€{Number(progresso.valor_proximo).toFixed(2)}):
                                              </span>
                                              <span className="font-bold text-amber-700" data-testid={`falta-turno-${dia.data}-${t.turno}`}>
                                                €{Number(progresso.falta).toFixed(2)}
                                              </span>
                                            </div>
                                            <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                                              <div
                                                className="bg-gradient-to-r from-primary to-primary/70 h-full transition-all"
                                                style={{
                                                  width: `${Math.min(100, Math.max(0, (t.total / progresso.valor_proximo) * 100))}%`
                                                }}
                                              />
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="text-xs text-green-700 flex items-center gap-1">
                                            <TrendingUp className="h-3.5 w-3.5" />
                                            Escalão máximo atingido neste turno!
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Detalhe por venda (hora + secção) — colapsível */}
                          <details className="text-xs">
                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground" data-testid={`detalhe-${dia.data}`}>
                              Ver detalhe por hora ({dia.vendas.length} entradas)
                            </summary>
                            <div className="space-y-2 mt-2">
                              {dia.vendas.map((venda, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-muted/20 rounded-md">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-[10px]">{venda.seccao}</Badge>
                                    {venda.hora !== undefined && (
                                      <span className="text-[10px] text-muted-foreground">
                                        {String(venda.hora).padStart(2, '0')}h — {venda.turno || ''}
                                      </span>
                                    )}
                                    <span className="text-[10px] text-muted-foreground">
                                      {venda.nr_docs} doc{venda.nr_docs !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  <p className="text-xs font-semibold text-success">€{venda.valor.toFixed(2)}</p>
                                </div>
                              ))}
                            </div>
                          </details>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Vista Global */}
      {activeView === 'global' && (
        <div className="space-y-6">
          {Object.keys(porSeccao).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Vendas por Secção - {MESES.find(m => m.value === filtroMes)?.label} {filtroAno}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(porSeccao).map(([seccao, dados]) => {
                    const media = dados.nr_docs > 0 ? dados.total / dados.nr_docs : 0;
                    return (
                      <Card key={seccao} className="bg-gradient-card border-primary/10">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <Badge variant="outline" className="text-sm">{seccao}</Badge>
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="space-y-2">
                            <div>
                              <p className="text-xs text-muted-foreground">Total</p>
                              <p className="text-2xl font-bold text-success">€{dados.total.toFixed(2)}</p>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-border/50">
                              <div>
                                <p className="text-xs text-muted-foreground">Documentos</p>
                                <p className="text-lg font-semibold">{dados.nr_docs}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-muted-foreground">Média</p>
                                <p className="text-lg font-semibold text-primary">€{media.toFixed(2)}</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {vendasFiltradas.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <ShoppingBag className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground">Nenhuma venda encontrada</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default MinhasVendasTab;
